// src/pages/settings/settings/ClinikoFormIssuesCard.tsx
import React, { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { SectionCard } from "../../../components/shared";

// API
import { listFormImportIssues, resolveFormImportIssue } from "../../../api/healthcare/Cliniko/ClinikoFormImportIssueApi";

// Types
import {
	CLINIKO_ISSUE_TYPES,
	ClinikoFormImportIssue,
	ClinikoFormImportIssueStatus,
	ClinikoFormImportIssueType,
} from "../../../types/healthcare/ClinikoFormImportIssueTypes";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";

const { Text, Paragraph } = Typography;

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: ClinikoFormImportIssueStatus; label: string }[] = [
	{ value: "unresolved", label: "Open" },
	{ value: "resolved", label: "Resolved" },
	{ value: "all", label: "All" },
];

const issueType = (key: string) => CLINIKO_ISSUE_TYPES.find((type) => type.key === key);

/** `duplicate_field_name` → `Duplicate field name`. The token is still shown underneath. */
const humaniseCode = (code: string): string => {
	const spaced = code.replace(/[_-]+/g, " ").trim();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatTimestamp = (value: string | null): string => (value ? dayjs(value).format("D MMM YYYY, HH:mm") : "—");

/**
 * ── The Cliniko form-import issues card (S11, ClickUp 14ygtkj65bx) ────────────────────────────
 *
 * Every row is an answer the form importer could not place, or a completed form it would not
 * write. Without this screen the ledger only moves the silence from a JSON report into a table:
 * the hourly sync keeps reporting success while clinical content is quietly dropped.
 *
 * ⚠️ **Nothing here shows what a patient answered.** The ledger stores ids, counts, the reason
 * token and the clinic-authored section/question names — enough to repair the template, and no
 * answer value exists to render.
 *
 * ⚠️ **Paging and filtering are SERVER-side.** A first full import can open thousands of rows, so
 * there is no client-side fetch-everything path to fall back to.
 */
const ClinikoFormIssuesCard: React.FC = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotifications();

	const [status, setStatus] = useState<ClinikoFormImportIssueStatus>("unresolved");
	const [typeFilter, setTypeFilter] = useState<ClinikoFormImportIssueType | undefined>(undefined);
	// One template defect usually explains many rows, so the template id is a filter, not just a column.
	const [templateFilter, setTemplateFilter] = useState<number | undefined>(undefined);
	const [page, setPage] = useState<number>(1);

	const issuesQuery = useQuery({
		queryKey: ["cliniko-form-import-issues", status, typeFilter, templateFilter, page],
		queryFn: () =>
			listFormImportIssues({
				status,
				issue_type: typeFilter,
				cliniko_template_id: templateFilter,
				page,
				per_page: PAGE_SIZE,
			}),
		// Holds the current page on screen while the next one loads, so the table does not blank out.
		placeholderData: keepPreviousData,
	});

	const resolveMutation = useMutation({
		mutationFn: (issueId: number) => resolveFormImportIssue(issueId),
		onSuccess: (response) => {
			queryClient.invalidateQueries({ queryKey: ["cliniko-form-import-issues"] });
			showSuccess(response.already_resolved ? "That issue was already resolved." : "Issue marked resolved.");
		},
		onError: (error: unknown) => showError(extractValidationErrors(error).join(" ")),
	});

	const applyFilter = (apply: () => void) => {
		apply();
		setPage(1);
	};

	const columns: ColumnsType<ClinikoFormImportIssue> = [
		{
			title: "Issue",
			dataIndex: "issue_type",
			key: "issue_type",
			render: (value: ClinikoFormImportIssueType) => (
				<Tag color={issueType(value)?.color}>{issueType(value)?.label ?? humaniseCode(value)}</Tag>
			),
		},
		{
			title: "Reason",
			dataIndex: "reason",
			key: "reason",
			render: (value: string) => (
				<Space direction="vertical" size={0}>
					<Text>{humaniseCode(value)}</Text>
					{/* The token is the join between a ledger row and the importer code that emitted it. */}
					<Text type="secondary" style={{ fontSize: 12 }}>
						{value}
					</Text>
				</Space>
			),
		},
		{
			title: "Section",
			dataIndex: "section_name",
			key: "section_name",
			render: (value: string) => value || <Text type="secondary">—</Text>,
		},
		{
			title: "Question",
			dataIndex: "question_name",
			key: "question_name",
			render: (value: string) => value || <Text type="secondary">—</Text>,
		},
		{
			title: "Cliniko form",
			dataIndex: "cliniko_patient_form_id",
			key: "cliniko_patient_form_id",
		},
		{
			title: "Template",
			dataIndex: "cliniko_template_id",
			key: "cliniko_template_id",
			render: (value: number | null) =>
				value === null ? (
					<Text type="secondary">—</Text>
				) : (
					<Button type="link" size="small" onClick={() => applyFilter(() => setTemplateFilter(value))}>
						{value}
					</Button>
				),
		},
		{
			title: "First seen",
			dataIndex: "first_seen_at",
			key: "first_seen_at",
			render: (value: string | null) => formatTimestamp(value),
		},
		{
			title: "Last seen",
			dataIndex: "last_seen_at",
			key: "last_seen_at",
			render: (value: string | null) => formatTimestamp(value),
		},
		{
			title: "",
			key: "actions",
			render: (_: unknown, issue: ClinikoFormImportIssue) =>
				issue.resolved_at ? (
					<Text type="secondary">{`Resolved ${formatTimestamp(issue.resolved_at)}`}</Text>
				) : (
					<Button
						size="small"
						icon={<CheckOutlined />}
						loading={resolveMutation.isPending && resolveMutation.variables === issue.id}
						onClick={() => resolveMutation.mutate(issue.id)}
					>
						Resolve
					</Button>
				),
		},
	];

	const rows = issuesQuery.data?.data ?? [];

	return (
		<SectionCard
			title="Cliniko form import issues"
			subtitle="Answers the form importer could not place. Each one is fixed by editing the form template, then re-running the import for that window"
			style={{ marginTop: 16 }}
			extra={
				<Space wrap>
					<Select<ClinikoFormImportIssueStatus>
						value={status}
						options={STATUS_OPTIONS}
						style={{ width: 130 }}
						aria-label="Resolved state"
						onChange={(value) => applyFilter(() => setStatus(value))}
					/>
					<Select<ClinikoFormImportIssueType | undefined>
						value={typeFilter}
						allowClear
						placeholder="All issue types"
						style={{ width: 190 }}
						aria-label="Issue type"
						options={CLINIKO_ISSUE_TYPES.map((type) => ({ value: type.key, label: type.label }))}
						onChange={(value) => applyFilter(() => setTypeFilter(value))}
					/>
				</Space>
			}
		>
			<Space direction="vertical" size="middle" style={{ width: "100%" }}>
				{templateFilter !== undefined && (
					<Tag closable color="blue" onClose={() => applyFilter(() => setTemplateFilter(undefined))}>
						{`Cliniko template ${templateFilter}`}
					</Tag>
				)}

				{typeFilter && <Alert type="info" showIcon message={issueType(typeFilter)?.guidance} />}

				<Table<ClinikoFormImportIssue>
					rowKey="id"
					size="small"
					columns={columns}
					dataSource={rows}
					loading={issuesQuery.isLoading}
					scroll={{ x: 1100 }}
					locale={{ emptyText: "No form import issues — every answer landed." }}
					expandable={{
						expandedRowRender: (issue) => (
							<Space direction="vertical" size={4} style={{ width: "100%" }}>
								<Paragraph style={{ marginBottom: 0 }}>{issueType(issue.issue_type)?.guidance}</Paragraph>
								{issue.answered !== null && (
									<Text type="secondary">{`${issue.matched ?? 0} of ${issue.answered} answers could be placed`}</Text>
								)}
								{issue.assigned_form_id !== null && (
									<Text type="secondary">{`Assigned form ${issue.assigned_form_id}`}</Text>
								)}
								{issue.resolved_by !== null && (
									<Text type="secondary">{`Resolved by user ${issue.resolved_by}`}</Text>
								)}
							</Space>
						),
					}}
					pagination={{
						current: issuesQuery.data?.page ?? page,
						pageSize: issuesQuery.data?.per_page ?? PAGE_SIZE,
						total: issuesQuery.data?.total ?? 0,
						showSizeChanger: false,
						showTotal: (total) => `${total} issue${total === 1 ? "" : "s"}`,
						onChange: (next) => setPage(next),
					}}
				/>
			</Space>
		</SectionCard>
	);
};

export default ClinikoFormIssuesCard;
